
import React, { useState, useEffect } from 'react';
import { Poll } from '@/types/group';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import PollItem from './PollItem';
import useEmblaCarousel from 'embla-carousel-react';

interface PollCarouselProps {
  polls: Poll[];
  onPollDeleted?: (pollId: string) => void;
}

const PollCarousel: React.FC<PollCarouselProps> = ({ polls, onPollDeleted }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false });
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      setCanScrollPrev(emblaApi.canScrollPrev());
      setCanScrollNext(emblaApi.canScrollNext());
      setCurrentIndex(emblaApi.selectedScrollSnap());
    };

    emblaApi.on('select', onSelect);
    // Initial check
    onSelect();

    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi]);

  const scrollPrev = () => emblaApi?.scrollPrev();
  const scrollNext = () => emblaApi?.scrollNext();

  return (
    <div className="relative">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex">
          {polls.map((poll) => (
            <div key={poll.id} className="flex-[0_0_100%] min-w-0">
              <PollItem poll={poll} onPollDeleted={onPollDeleted} />
            </div>
          ))}
        </div>
      </div>
      
      {/* Navigation buttons */}
      {canScrollPrev && (
        <Button 
          variant="outline" 
          size="icon" 
          className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 z-10 bg-white shadow-md rounded-full"
          onClick={scrollPrev}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
      )}
      
      {canScrollNext && (
        <Button 
          variant="outline" 
          size="icon" 
          className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-10 bg-white shadow-md rounded-full"
          onClick={scrollNext}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      )}
      
      {/* Pagination indicators */}
      {polls.length > 1 && (
        <div className="flex justify-center gap-1 mt-3">
          {polls.map((_, index) => (
            <div 
              key={index}
              className={`h-2 w-2 rounded-full transition-colors ${
                currentIndex === index ? 'bg-primary' : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default PollCarousel;
